import type { ReactNode } from 'react';

import { haptic } from '@kilat/ui';

import { LocaleLink as Link } from '../lib/locale-link.js';
import { useMarketingSeo } from '../lib/use-marketing-seo.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useTrialDays } from '../lib/use-trial-days.js';
import { useI18n } from '../lib/use-i18n.js';
import { useCountUp, useReveal, useTilt } from '../lib/use-motion.js';
import { RevealBlock } from '../components/reveal-block.js';
import { MarketingShell } from '../components/marketing-shell.js';
import { HeroScene } from '../components/hero-scene.js';
import { AgentPhoneMock, CustomerPhoneMock, QueueBoardMock } from '../components/device-mockups.js';
import '../styles/home.css';

type T = (key: string, vars?: Record<string, string | number>) => string;

/**
 * Marketing home — cinematic interactive rebuild.
 *
 * The visual language roots in "Kilat" (flash / shine): a dark, luminous hero
 * with a real, touch-interactive WebGL bolt (see HeroScene → hero-3d, code-split
 * + gated), floating product mockups (the operator queue board + a white-label
 * customer phone), animated
 * trust counters, and scroll-revealed product/benefit sections. Every effect is
 * progressive enhancement layered over real, prerendered DOM — the SEO-critical
 * headings, copy, CTAs and links render server-side and are untouched by the
 * motion layer; `prefers-reduced-motion` and the prerender both collapse to a
 * static, valid end state.
 */
export function Home() {
  const brand = usePlatformBrand();
  const { t, platformBrandCopy } = useI18n();
  const trialDays = useTrialDays();
  useMarketingSeo({
    title: t('marketing.home.metaTitle', { brand: brand.name }),
    description: t('marketing.home.metaDescription', { brand: brand.name }),
  });

  return (
    <MarketingShell>
      <Hero
        badge={platformBrandCopy('pitchOneLiner')}
        headline={platformBrandCopy('heroHeadline')}
        subheadline={platformBrandCopy('heroSubheadline')}
        t={t}
        trialDays={trialDays}
      />
      <StatStrip t={t} />
      <Showcase t={t} brandName={brand.shortName} />
      <Pillars t={t} brandName={brand.name} />
      <PainGain t={t} />
      <BottomCta
        heading={t('marketing.home.bottomHeading', { brand: brand.name, days: trialDays })}
        body={t('marketing.home.bottomBody')}
        startTrialLabel={t('marketing.cta.startTrial')}
        seePricingLabel={t('marketing.cta.seePricing')}
      />
    </MarketingShell>
  );
}

// ── hero ────────────────────────────────────────────────────────────────────

function Hero({
  badge,
  headline,
  subheadline,
  t,
  trialDays,
}: {
  readonly badge: string;
  readonly headline: string;
  readonly subheadline: string;
  readonly t: T;
  /** Passed down rather than read here: Hero takes `t` as a prop too, and one
      component owning both keeps the hero a pure presentational unit. */
  readonly trialDays: number;
}) {
  const tiltRef = useTilt<HTMLDivElement>(7);
  return (
    <section className="hero on-dark shine-host">
      <HeroScene />
      <div className="hero__inner">
        <div>
          <span className="hero__eyebrow glass hero-anim">
            <BoltIcon />
            {badge}
          </span>
          <h1 className="hero__title hero-anim" style={{ animationDelay: '60ms' }}>
            {headline}
          </h1>
          <p className="hero__sub hero-anim" style={{ animationDelay: '140ms' }}>
            {subheadline}
          </p>
          <div className="hero__cta-row hero-anim" style={{ animationDelay: '220ms' }}>
            <Link to="/signup" className="btn btn--primary lift" onClick={() => haptic.tap()}>
              {t('marketing.cta.startTrial')}
              <ArrowIcon />
            </Link>
            <Link to="/pricing" className="btn btn--ghost lift" onClick={() => haptic.tap()}>
              {t('marketing.cta.seePricing')}
            </Link>
          </div>
          <p className="hero__note hero-anim" style={{ animationDelay: '300ms' }}>
            {t('marketing.cta.trialTagline', { days: trialDays })}
          </p>
        </div>

        <div className="device-stage hero-anim" style={{ animationDelay: '260ms' }}>
          <div className="device-cluster" ref={tiltRef}>
            <QueueBoardMock className="device-cluster__tablet kilat-float-slow" />
            <CustomerPhoneMock className="device-cluster__phone kilat-float" />
          </div>
        </div>
      </div>
    </section>
  );
}

// ── stat strip ──────────────────────────────────────────────────────────────

const STATS: readonly { value: number; suffix: string; labelKey: string }[] = [
  { value: 4, suffix: '', labelKey: 'marketing.home.stat1Label' },
  { value: 14, suffix: '', labelKey: 'marketing.home.stat2Label' },
  { value: 3, suffix: '', labelKey: 'marketing.home.stat3Label' },
  { value: 100, suffix: '%', labelKey: 'marketing.home.stat4Label' },
];

function StatStrip({ t }: { readonly t: T }) {
  const { ref, inView } = useReveal<HTMLDivElement>();
  return (
    <section className="section" style={{ paddingTop: 'clamp(2rem,4vw,3.5rem)' }}>
      <div className="section__inner">
        <div
          ref={ref}
          className={`reveal reveal-scale stat-strip glass${inView ? ' is-in' : ''}`}
          style={statCardStyle}
        >
          {STATS.map((s, i) => (
            <Stat key={s.labelKey} stat={s} inView={inView} index={i} label={t(s.labelKey)} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Stat({
  stat,
  inView,
  index,
  label,
}: {
  readonly stat: { value: number; suffix: string; labelKey: string };
  readonly inView: boolean;
  readonly index: number;
  readonly label: string;
}) {
  const count = useCountUp(stat.value, inView, 1200 + index * 120);
  return (
    <div className="stat" data-stagger style={{ animationDelay: `${index * 70}ms` }}>
      <span className="stat__value">
        {count}
        {stat.suffix}
      </span>
      <span className="stat__label">{label}</span>
    </div>
  );
}

// ── product showcase ─────────────────────────────────────────────────────────

function Showcase({ t, brandName }: { readonly t: T; readonly brandName: string }) {
  return (
    <section className="section">
      <div className="section__inner">
        <RevealBlock className="section__head">
          <p className="eyebrow">{t('marketing.home.showcaseEyebrow')}</p>
          <h2 className="section__title">{t('marketing.home.showcaseHeading')}</h2>
          <p className="section__lead">{t('marketing.home.showcaseLead')}</p>
        </RevealBlock>

        <div style={{ marginTop: 'clamp(2.5rem,5vw,4rem)' }}>
          <ShowcaseRow
            eyebrow={t('marketing.home.showcaseDashEyebrow')}
            title={t('marketing.home.showcaseDashTitle')}
            body={t('marketing.home.showcaseDashBody')}
            bullets={[
              t('marketing.home.dashBullet1'),
              t('marketing.home.dashBullet2'),
              t('marketing.home.dashBullet3'),
            ]}
            media={<QueueBoardMock />}
          />
          <ShowcaseRow
            reverse
            eyebrow={t('marketing.home.showcaseCustEyebrow')}
            title={t('marketing.home.showcaseCustTitle')}
            body={t('marketing.home.showcaseCustBody')}
            bullets={[
              t('marketing.home.custBullet1', { brand: brandName }),
              t('marketing.home.custBullet2'),
              t('marketing.home.custBullet3'),
            ]}
            media={<CustomerPhoneMock />}
          />
          <ShowcaseRow
            eyebrow={t('marketing.home.showcaseAgentEyebrow')}
            title={t('marketing.home.showcaseAgentTitle')}
            body={t('marketing.home.showcaseAgentBody')}
            bullets={[
              t('marketing.home.agentBullet1'),
              t('marketing.home.agentBullet2'),
              t('marketing.home.agentBullet3'),
            ]}
            media={<AgentPhoneMock />}
          />
        </div>
      </div>
    </section>
  );
}

function ShowcaseRow({
  eyebrow,
  title,
  body,
  bullets,
  media,
  reverse = false,
}: {
  readonly eyebrow: string;
  readonly title: string;
  readonly body: string;
  readonly bullets: readonly string[];
  readonly media: ReactNode;
  readonly reverse?: boolean;
}) {
  const { ref, inView } = useReveal<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={`reveal showcase-row${reverse ? ' showcase-row--reverse' : ''}${
        inView ? ' is-in' : ''
      }`}
    >
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h3 className="section__title" style={{ fontSize: 'clamp(1.5rem,1.8vw+1rem,2.1rem)' }}>
          {title}
        </h3>
        <p className="section__lead">{body}</p>
        <ul className="feature-list">
          {bullets.map((b) => (
            <li key={b}>
              <span className="tick" aria-hidden>
                <CheckIcon />
              </span>
              {b}
            </li>
          ))}
        </ul>
      </div>
      <div className="showcase-row__media">{media}</div>
    </div>
  );
}

// ── three pillars ────────────────────────────────────────────────────────────

function Pillars({ t, brandName }: { readonly t: T; readonly brandName: string }) {
  const pillars = [
    {
      icon: <QueueIcon />,
      title: t('marketing.home.pillar1Title'),
      body: t('marketing.home.pillar1Body'),
      bullets: [
        t('marketing.home.pillar1Bullet1'),
        t('marketing.home.pillar1Bullet2'),
        t('marketing.home.pillar1Bullet3'),
      ],
    },
    {
      icon: <HeartIcon />,
      title: t('marketing.home.pillar2Title'),
      body: t('marketing.home.pillar2Body'),
      bullets: [
        t('marketing.home.pillar2Bullet1'),
        t('marketing.home.pillar2Bullet2'),
        t('marketing.home.pillar2Bullet3'),
      ],
    },
    {
      icon: <PhoneIcon />,
      title: t('marketing.home.pillar3Title'),
      body: t('marketing.home.pillar3Body', { brand: brandName }),
      bullets: [
        t('marketing.home.pillar3Bullet1'),
        t('marketing.home.pillar3Bullet2'),
        t('marketing.home.pillar3Bullet3'),
      ],
    },
  ];
  return (
    <section className="section" style={{ background: 'var(--color-surface-2)' }}>
      <div className="section__inner">
        <RevealBlock className="section__head">
          <p className="eyebrow">{t('marketing.home.pillarsEyebrow')}</p>
          <h2 className="section__title">{t('marketing.home.pillarsHeading')}</h2>
          <p className="section__lead">{t('marketing.home.pillarsLead')}</p>
        </RevealBlock>
        <RevealBlock className="card-grid" style={{ marginTop: 'clamp(2rem,4vw,3rem)' }}>
          {pillars.map((p, i) => (
            <article
              key={p.title}
              className="g-card lift"
              data-stagger
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <span className="g-card__icon" aria-hidden>
                {p.icon}
              </span>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, lineHeight: 1.25 }}>
                {p.title}
              </h3>
              <p
                style={{
                  margin: 'var(--space-3) 0 0',
                  fontSize: '0.92rem',
                  color: 'var(--color-on-surface-muted)',
                  lineHeight: 1.55,
                }}
              >
                {p.body}
              </p>
              <ul className="feature-list" style={{ marginTop: 'var(--space-4)' }}>
                {p.bullets.map((b) => (
                  <li key={b}>
                    <span className="tick" aria-hidden>
                      <CheckIcon />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </RevealBlock>
      </div>
    </section>
  );
}

// ── pain → gain ──────────────────────────────────────────────────────────────

function PainGain({ t }: { readonly t: T }) {
  const rows = [1, 2, 3, 4, 5, 6].map((n) => ({
    pain: t(`marketing.home.pain${n}`),
    gain: t(`marketing.home.gain${n}`),
  }));
  return (
    <section className="section">
      <div className="section__inner">
        <RevealBlock className="section__head">
          <p className="eyebrow">{t('marketing.home.painEyebrow')}</p>
          <h2 className="section__title">{t('marketing.home.painHeading')}</h2>
          <p className="section__lead">{t('marketing.home.painLead')}</p>
        </RevealBlock>
        <RevealBlock className="painmap" style={{ marginTop: 'clamp(2rem,4vw,3rem)' }}>
          {rows.map((row, i) => (
            <div
              className="painrow"
              key={row.gain}
              data-stagger
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <p className="painrow__pain">{row.pain}</p>
              <span className="painrow__arrow" aria-hidden>
                <ArrowIcon />
              </span>
              <p className="painrow__gain">{row.gain}</p>
            </div>
          ))}
        </RevealBlock>
      </div>
    </section>
  );
}

// ── bottom CTA ───────────────────────────────────────────────────────────────

function BottomCta({
  heading,
  body,
  startTrialLabel,
  seePricingLabel,
}: {
  readonly heading: string;
  readonly body: string;
  readonly startTrialLabel: string;
  readonly seePricingLabel: string;
}) {
  return (
    <section className="section">
      <div className="section__inner">
        <RevealBlock className="cta-panel on-dark shine-host reveal-scale">
          <h2 className="cta-panel__title">{heading}</h2>
          <p className="cta-panel__body">{body}</p>
          <div
            style={{
              marginTop: 'var(--space-6)',
              display: 'flex',
              gap: 'var(--space-3)',
              justifyContent: 'center',
              flexWrap: 'wrap',
              position: 'relative',
              zIndex: 2,
            }}
          >
            <Link to="/signup" className="btn btn--primary lift" onClick={() => haptic.tap()}>
              {startTrialLabel}
              <ArrowIcon />
            </Link>
            <Link to="/pricing" className="btn btn--ghost lift" onClick={() => haptic.tap()}>
              {seePricingLabel}
            </Link>
          </div>
        </RevealBlock>
      </div>
    </section>
  );
}

const statCardStyle: React.CSSProperties = {
  padding: 'clamp(1rem, 2.5vw, 1.75rem)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-2)',
};

// ── icons (decorative, aria-hidden) ──────────────────────────────────────────

function BoltIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden focusable="false">
      <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z" fill="var(--kilat-lume)" />
    </svg>
  );
}
function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden focusable="false" fill="none">
      <path
        d="M5 12h14m0 0-6-6m6 6-6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden focusable="false" fill="none">
      <path
        d="m5 12 4.5 4.5L19 7"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function QueueIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden focusable="false" fill="none">
      <rect x="3" y="4" width="5" height="16" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
      <rect x="9.5" y="4" width="5" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
      <rect x="16" y="4" width="5" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}
function HeartIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden focusable="false" fill="none">
      <path
        d="M12 20s-7-4.35-7-9.5A3.5 3.5 0 0 1 12 7a3.5 3.5 0 0 1 7 3.5C19 15.65 12 20 12 20Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function PhoneIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden focusable="false" fill="none">
      <rect x="7" y="2.5" width="10" height="19" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M11 18.5h2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
